import React, { useState, useContext } from "react";
import { AppContext } from "../../contexts/AppContext";
import { Link, useLocation } from "react-router-dom";
import { FaWhatsapp, FaTwitter, FaInstagram, FaYoutube } from "react-icons/fa";
import { FiMail, FiMapPin, FiArrowUp, FiSend } from "react-icons/fi";
import { ThemeContext, ThemeContextData } from "../../contexts/ThemeContext";
import { subscribeToNewsletter } from "../../utils/api";
import { motion } from "framer-motion";
import ScrollReveal from "../ui/ScrollReveal";

const quickLinks = [
  { title: "Home", url: "/" },
  { title: "About Us", url: "/about-us" },
  { title: "Contact Us", url: "/contact-us" },
  { title: "Blogs", url: "/blogs" },
  { title: "Terms & Conditions", url: "/terms-and-conditions" },
];

const programLinks = [
  { title: "All Programs", url: "/programs" },
  { title: "Explore Programs", url: "/programs/explore" },
  { title: "Group Calisthenics", url: "/group-calisthenics" },
  { title: "90-Day Challenge", url: "/90-day-challenge" },
  { title: "Pre Recorded Courses", url: "/courses" },
];

function Footer() {
  const appContext = useContext(AppContext);
  const themeContext = useContext(ThemeContext);
  const theme = ThemeContextData[themeContext];
  const { pathname } = useLocation();
  // Hide the floating WhatsApp button on the course-detail page where it
  // overlaps the video player.
  const hideWhatsappFab = /^\/courses\/[^/]+$/.test(pathname);
  const [fullName, setFullName] = useState("");
  const [userContact, setUserContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubscribe = async () => {
    if (!fullName.trim()) {
      setMessage({ type: "error", text: "Please provide your full name" });
      return;
    }
    if (!userContact.trim()) {
      setMessage({ type: "error", text: "Please provide an email address or mobile number" });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const isEmail = userContact.includes("@");
      const data = isEmail ? { fullName, email: userContact } : { fullName, mobileNumber: userContact };
      const response = await subscribeToNewsletter(data);
      setLoading(false);
      setMessage({ type: "success", text: response.message || "Subscribed successfully!" });
      setTimeout(() => {
        setFullName("");
        setUserContact("");
        setMessage(null);
      }, 3000);
    } catch (error) {
      setLoading(false);
      setMessage({ type: "error", text: error.message || "Failed to subscribe. Please try again later." });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const socialLinks = [
    { icon: FaTwitter, url: appContext.socialMedia.find((s) => s.platform === "twitter")?.url, label: "Twitter" },
    { icon: FaInstagram, url: appContext.socialMedia.find((s) => s.platform === "instagram")?.url, label: "Instagram" },
    { icon: FaYoutube, url: appContext.socialMedia.find((s) => s.platform === "youtube")?.url, label: "YouTube" },
  ];

  return (
    <footer className={`${theme.textNormal} ${theme.backgroundSecondary} relative pb-16 md:pb-0`}>
      {/* WhatsApp FAB */}
      {!hideWhatsappFab && (
        <a
          href={appContext.footerData.whatsappCTA.link}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-20 md:bottom-6 right-5 z-30 group"
          aria-label="Chat on WhatsApp"
        >
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="relative bg-green-500 hover:bg-green-600 text-white p-3.5 rounded-full shadow-lg transition-colors duration-200"
          >
            <FaWhatsapp className="w-7 h-7" />
            <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20" />
          </motion.div>
        </a>
      )}

      {/* Back to Top */}
      <div className="flex justify-center -mt-6 relative z-10">
        <motion.button
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.95 }}
          onClick={scrollToTop}
          className="bg-brand-500 hover:bg-brand-600 text-white p-3 rounded-full shadow-lg transition-colors duration-200 focus-ring"
          aria-label="Back to top"
        >
          <FiArrowUp className="w-5 h-5" />
        </motion.button>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <ScrollReveal animation="fade-up">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Brand Column */}
            <div className="sm:col-span-2 lg:col-span-1">
              <Link to="/" className="inline-block mb-4 focus-ring">
                <img src={appContext.logo} className="h-12" alt={appContext.name} width={120} height={48} loading="lazy" />
              </Link>
              <p className={`text-sm leading-relaxed mb-6 max-w-xs ${theme.textNormalHover}`}>
                India&rsquo;s premier calisthenics platform. Master bodyweight strength training with expert coaching.
              </p>
              <div className="flex items-center space-x-3">
                {socialLinks.map((social, idx) => (
                  social.url && (
                    <motion.a
                      key={idx}
                      whileHover={{ y: -3, scale: 1.1 }}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-500/10 hover:bg-brand-500 hover:text-white text-brand-500 transition-all duration-200"
                    >
                      <social.icon className="w-4 h-4" />
                    </motion.a>
                  )
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className={`font-heading font-bold text-sm tracking-wider uppercase mb-4 ${theme.textFocus}`}>
                Quick Links
              </h3>
              <nav className="space-y-2.5">
                {quickLinks.map((link) => (
                  <Link
                    key={link.url}
                    to={link.url}
                    className={`block text-sm hover:text-brand-500 hover:translate-x-1 transition-all duration-200`}
                  >
                    {link.title}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Programs */}
            <div>
              <h3 className={`font-heading font-bold text-sm tracking-wider uppercase mb-4 ${theme.textFocus}`}>
                Programs
              </h3>
              <nav className="space-y-2.5">
                {programLinks.map((link) => (
                  <Link
                    key={link.url}
                    to={link.url}
                    className={`block text-sm hover:text-brand-500 hover:translate-x-1 transition-all duration-200`}
                  >
                    {link.title}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Newsletter */}
            <div>
              <h3 className={`font-heading font-bold text-sm tracking-wider uppercase mb-4 ${theme.textFocus}`}>
                Stay Updated
              </h3>
              <p className="text-sm mb-4">
                Get fitness tips, new programs, and exclusive offers.
              </p>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Your name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all"
                />
                <input
                  type="text"
                  placeholder="Email or mobile number"
                  value={userContact}
                  onChange={(e) => setUserContact(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-[var(--color-bg-input)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all"
                />
                {message && (
                  <p className={`text-xs ${message.type === "success" ? "text-green-500" : "text-red-500"}`}>
                    {message.text}
                  </p>
                )}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSubscribe}
                  disabled={loading}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 transition-all duration-200 ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <FiSend className="w-4 h-4" />
                  {loading ? "Subscribing..." : "Subscribe"}
                </motion.button>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* Bottom Bar */}
      <div className={`${theme.backgroundTertiary} bg-opacity-75 border-t border-[var(--color-border-default)] ${theme.textFocus}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="focus-ring">
                  <img src={appContext.logo} className="h-8" alt={appContext.name} width={80} height={32} loading="lazy" />
                </Link>
              <p className="text-xs">
                © {new Date().getFullYear()} {appContext.name} — {appContext.information.owner}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <FiMail className="w-3.5 h-3.5" />
                {appContext.information.email}
              </span>
              <span className="hidden sm:flex items-center gap-1.5">
                <FiMapPin className="w-3.5 h-3.5 flex-shrink-0" />
                Mumbai, India
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
